import Bowser from "bowser";

class DeviceUtility {
	static get browser() {
		const navigator = window.navigator;
		if (!navigator) return "unknown";
		const parser = Bowser.getParser(window.navigator.userAgent);
		return parser.getBrowserName();
	}

	static get browserVersion() {
		const navigator = window.navigator;
		if (!navigator) return "unknown";
		const parser = Bowser.getParser(window.navigator.userAgent);
		return parser.getBrowser().version;
	}

	static get isAndroid() {
		const parser = Bowser.getParser(window.navigator.userAgent);
		return parser.getOSName(true) === "android";
	}

	static get isDesktop() {
		return this.getDeviceType() === "desktop";
	}

	static get isFirefox() {
		return this.browser === "Firefox";
	}

	static get isSlimPhone() {
		return window.matchMedia("(max-width: 350px)").matches;
	}

	static get isSmallPhone() {
		return window.matchMedia("(max-width: 400px)").matches;
	}

	static get isMobile() {
		return this.getDeviceType() === "mobile";
	}

	static get isSafari() {
		return this.browser === "Safari";
	}

	static get isAndroidWebview() {
		return this.isAndroid && /wv/i.test(window.navigator.userAgent);
	}

	static get isPwaInstalled() {
		return window.matchMedia("(display-mode: standalone)").matches;
	}

	static get isIOS() {
		const parser = Bowser.getParser(window.navigator.userAgent);
		return parser.getOSName(true) === "ios";
	}

	// True on any iOS/iPadOS browser. Bowser's isIOS alone misses iPadOS Safari, which sends a
	// desktop ("Macintosh") UA in its default desktop-site mode; isIpad covers that case. Use this
	// for behaviour that must be uniform across every iOS/iPadOS browser (all WebKit under the hood)
	// — e.g. the geolocation permission re-prompt, which requires a full page reload on WebKit.
	static get isIosOrIpados() {
		return this.isIOS || this.isIpad;
	}

	static get isIpad() {
		const result = Bowser.parse(window.navigator.userAgent);

		if ((result.platform.model && result.platform.model.toLocaleLowerCase() === "ipad") || /ipad/i.test(navigator.userAgent)) {
			return true;
		}

		if (this.isTablet && /mac/i.test(navigator.userAgent)) {
			return true;
		}

		return false;
	}

	static get isTablet() {
		return this.getDeviceType() === "tablet";
	}

	// Touch form factors (phone or tablet). Inline-rendering paths gate on this because a floating
	// overlay menu collides with the on-screen keyboard on touch devices (LSP-16443 / LSP-16445).
	static get isMobileOrTablet() {
		return this.isMobile || this.isTablet;
	}

	static get fallbackOrientation() {
		const isPortrait = window.innerHeight > window.innerWidth;
		return isPortrait ? "portrait" : "landscape";
	}

	static get isLandscape() {
		const isLandscape = window.matchMedia("(orientation: landscape)").matches;
		return isLandscape;
	}

	static get isTouch() {
		const isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;
		return isTouch;
	}

	static getDeviceType = function () {
		const navigator = window.navigator;
		if (!navigator) return "unknown";

		const result = Bowser.parse(window.navigator.userAgent);
		const platformType = result.platform.type?.toLocaleLowerCase();

		// Trust Bowser's UA-based mobile detection first — it is orientation-independent.
		// Without this early return, phones in landscape can match the iPad media query below
		// (their landscape device-width falls into 768-1366px) and get misclassified as tablets.
		if (platformType === "mobile") {
			return "mobile";
		}

		if (
			(result.platform.model && result.platform.model.toLocaleLowerCase() === "ipad") ||
			window.matchMedia(
				"only screen and (min-device-width: 768px) and (max-device-width: 1366px) and (-webkit-min-device-pixel-ratio: 2)"
			).matches
		) {
			return "tablet";
		}

		return !platformType || platformType === undefined ? "unknown" : platformType;
	};
}

export default DeviceUtility;
